import React, { useEffect, useMemo } from 'react';
import {
  Box,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Paper,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Autocomplete,
  Chip,
} from '@mui/material';
import countryList from 'react-select-country-list';

const OPTIMIZATION_GOALS = [
  {
    value: 'APP_INSTALLS',
    label: 'App Installs',
    description: 'Optimize for people likely to install your app',
  },
  {
    value: 'AD_RECALL_LIFT',
    label: 'Ad Recall Lift',
    description: 'Optimize for people likely to remember your ads',
  },
  {
    value: 'ENGAGED_USERS',
    label: 'Engaged Users',
    description: 'Optimize for people likely to engage with your content',
  },
  {
    value: 'EVENT_RESPONSES',
    label: 'Event Responses',
    description: 'Optimize for people likely to respond to your event',
  },
  {
    value: 'IMPRESSIONS',
    label: 'Impressions',
    description: 'Optimize for maximum impressions',
  },
  {
    value: 'LEAD_GENERATION',
    label: 'Lead Generation',
    description: 'Optimize for people likely to submit your lead form',
  },
  {
    value: 'LINK_CLICKS',
    label: 'Link Clicks',
    description: 'Optimize for people likely to click your links',
  },
  {
    value: 'OFFSITE_CONVERSIONS',
    label: 'Offsite Conversions',
    description: 'Optimize for people likely to convert on your website',
  },
  {
    value: 'PAGE_LIKES',
    label: 'Page Likes',
    description: 'Optimize for people likely to like your page',
  },
  {
    value: 'POST_ENGAGEMENT',
    label: 'Post Engagement',
    description: 'Optimize for people likely to engage with your posts',
  },
  {
    value: 'REACH',
    label: 'Reach',
    description: 'Optimize for maximum reach',
  },
  {
    value: 'VALUE',
    label: 'Value',
    description: 'Optimize for highest value conversions',
  },
  {
    value: 'THRUPLAY',
    label: 'ThruPlay',
    description: 'Optimize for people likely to watch your video ads to completion',
  }
];

const BILLING_EVENTS = [
  {
    value: 'IMPRESSIONS',
    label: 'Impressions (CPM)',
    description: 'Pay for impressions delivered',
  },
  {
    value: 'LINK_CLICKS',
    label: 'Link Click (CPC)',
    description: 'Pay for clicks on your ads',
  },
];

const BID_STRATEGIES = [
  {
    value: 'LOWEST_COST_WITHOUT_CAP',
    label: 'Lowest Cost',
    description: 'Get the most results at the lowest cost',
  },
  {
    value: 'LOWEST_COST_WITH_BID_CAP',
    label: 'Cost Cap',
    description: 'Control your cost per result',
  },
];

const DEFAULT_TARGETING = {
  countries: ['US'],
  interests: ['all'],
};

// Popular countries for Facebook ads
const POPULAR_COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'CA', name: 'Canada' },
  { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'ES', name: 'Spain' },
  { code: 'IT', name: 'Italy' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'BR', name: 'Brazil' },
  { code: 'MX', name: 'Mexico' },
  { code: 'JP', name: 'Japan' },
  { code: 'IN', name: 'India' },
  { code: 'SG', name: 'Singapore' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'SE', name: 'Sweden' },
  { code: 'NO', name: 'Norway' },
  { code: 'DK', name: 'Denmark' },
  { code: 'FI', name: 'Finland' },
  { code: 'BE', name: 'Belgium' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'AT', name: 'Austria' },
  { code: 'IE', name: 'Ireland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'PL', name: 'Poland' },
];

const AdSetConfigurator = ({ adSetData, onAdSetChange }) => {
  // Get all countries from react-select-country-list
  const allCountries = useMemo(() => {
    const countries = countryList().getData();
    // Convert to format we need (code and name)
    return countries.map(country => ({
      code: country.value,
      name: country.label
    }));
  }, []);

  // Initialize targeting on mount or when adSetData changes
  useEffect(() => {
    // Check if targeting is missing or incomplete
    if (!adSetData.targeting || !adSetData.targeting.countries || !adSetData.targeting.countries.length) {
      onAdSetChange({
        ...adSetData,
        targeting: DEFAULT_TARGETING,
      });
    }
  }, [adSetData, onAdSetChange]); // Watch for adSetData changes

  // Handle ad set name change
  const handleNameChange = (event) => {
    onAdSetChange({
      ...adSetData,
      name: event.target.value,
    });
  };

  // Handle country selection change
  const handleCountriesChange = (event, newValue) => {
    const countryCodes = newValue.map(country => country.code);
    onAdSetChange({
      ...adSetData,
      targeting: {
        ...adSetData.targeting,
        countries: countryCodes,
      },
    });
  };

  // Handle budget change
  const handleBudgetChange = (event) => {
    const value = parseFloat(event.target.value);
    onAdSetChange({
      ...adSetData,
      budget: isNaN(value) ? 0 : value,
    });
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Ad Set Name */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Ad Set Name"
            value={adSetData.name}
            onChange={handleNameChange}
            helperText={!adSetData.name ? 'Ad set name is required' : ''}
            error={!adSetData.name}
            required
          />
        </Grid>

        {/* Budget */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="number"
            label="Daily Budget"
            value={adSetData.budget || ''}
            onChange={handleBudgetChange}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            required
          />
        </Grid>

        {/* Targeting Configuration */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
            <Typography variant="subtitle2" gutterBottom>
              Targeting Configuration
            </Typography>
            
            {/* Country Selection */}
            <Box sx={{ mb: 3 }}>
              <Autocomplete
                multiple
                options={allCountries}
                getOptionLabel={(option) => option.name}
                value={allCountries.filter(country => 
                  adSetData.targeting?.countries?.includes(country.code)
                )}
                onChange={handleCountriesChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Target Countries"
                    placeholder="Select countries to target"
                    helperText="Choose one or more countries for your ad targeting"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option.name}
                      {...getTagProps({ index })}
                      key={option.code}
                    />
                  ))
                }
                sx={{ mb: 2 }}
              />
              
              {/* Quick Country Presets */}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Quick presets:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip
                  label="English Speaking"
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const englishSpeaking = ['US', 'GB', 'CA', 'AU', 'NZ', 'IE'];
                    onAdSetChange({
                      ...adSetData,
                      targeting: {
                        ...adSetData.targeting,
                        countries: englishSpeaking,
                      },
                    });
                  }}
                />
                <Chip
                  label="Europe"
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const europe = ['GB', 'DE', 'FR', 'ES', 'IT', 'NL', 'SE', 'NO', 'DK', 'FI', 'BE', 'CH', 'AT', 'IE', 'PT', 'PL'];
                    onAdSetChange({
                      ...adSetData,
                      targeting: {
                        ...adSetData.targeting,
                        countries: europe,
                      },
                    });
                  }}
                />
                <Chip
                  label="North America"
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const northAmerica = ['US', 'CA', 'MX'];
                    onAdSetChange({
                      ...adSetData,
                      targeting: {
                        ...adSetData.targeting,
                        countries: northAmerica,
                      },
                    });
                  }}
                />
              </Box>
            </Box>

            <List dense>
              <ListItem>
                <ListItemText
                  primary="Interests"
                  secondary="All interests"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Devices"
                  secondary="Mobile devices only (iOS and Android)"
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Summary */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
            <Typography variant="subtitle2" gutterBottom>
              Ad Set Summary
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="Name"
                  secondary={adSetData.name || 'Not set'}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Daily Budget"
                  secondary={adSetData.budget ? `$${adSetData.budget.toFixed(2)}` : 'Not set'}
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdSetConfigurator; 